import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Reservation, ReservationStatus } from '@reims-local/types';
import { reservationApi } from '../lib/api';

export function Reservations() {
  const [rows, setRows] = useState<Array<{ reservation: Reservation; buyer: { id: string; name: string | null; email: string; phone: string | null; no_show_count: number } }>>([]);
  const [statusFilter, setStatusFilter] = useState<ReservationStatus | ''>('confirmed');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Paris' }).format(new Date());
      const res = await reservationApi.list({
        status: statusFilter || undefined,
        from: today,
      });
      setRows(res.data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const intv = setInterval(() => void load(), 60_000);
    return () => clearInterval(intv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Groupe par date
  const byDate = new Map<string, typeof rows>();
  for (const r of rows) {
    const key = r.reservation.date;
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key)!.push(r);
  }

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Réservations ({rows.length})</h2>
        <div className="spacer" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ReservationStatus | '')}>
          <option value="">Tous statuts</option>
          <option value="pending">En attente</option>
          <option value="confirmed">Confirmées</option>
          <option value="cancelled">Annulées</option>
          <option value="no_show">No-shows</option>
        </select>
        <button onClick={() => void load()}>Rafraîchir</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p>Chargement…</p>
      ) : rows.length === 0 ? (
        <div className="card"><p>Aucune réservation à venir.</p></div>
      ) : (
        Array.from(byDate.entries()).map(([date, list]) => (
          <div key={date} className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ marginTop: 0 }}>
              {formatDateLong(date)} — {list.reduce((s, r) => s + (r.reservation.covers ?? 0), 0)} couverts
            </h3>
            <table>
              <thead>
                <tr>
                  <th>Heure</th>
                  <th>N°</th>
                  <th>Couverts</th>
                  <th>Client</th>
                  <th>Demande</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {list.map(({ reservation, buyer }) => (
                  <tr key={reservation.id}>
                    <td><strong>{formatTime(reservation.time)}</strong></td>
                    <td><Link to={`/reservations/${reservation.id}`}>{reservation.reservation_number}</Link></td>
                    <td>{reservation.covers}</td>
                    <td>
                      {buyer.name ?? buyer.email}
                      {buyer.no_show_count >= 2 && <span className="badge red" style={{ marginLeft: 6 }}>⚠ {buyer.no_show_count} no-shows</span>}
                      <div className="muted">{buyer.phone ?? '—'}</div>
                    </td>
                    <td className="muted" style={{ maxWidth: 200 }}>{reservation.special_request ?? '—'}</td>
                    <td><span className={`badge ${statusClass(reservation.status as ReservationStatus)}`}>{statusLabel(reservation.status as ReservationStatus)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </>
  );
}

export function ReservationDetail({ reservationId }: { reservationId: string }) {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof reservationApi.get>>['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelInput, setShowCancelInput] = useState(false);

  async function load() {
    try {
      const res = await reservationApi.get(reservationId);
      setDetail(res.data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [reservationId]);

  async function changeStatus(status: 'cancelled' | 'no_show') {
    setUpdating(true);
    try {
      await reservationApi.patch(reservationId, status, status === 'cancelled' ? cancelReason : undefined);
      await load();
      setShowCancelInput(false);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setUpdating(false);
    }
  }

  if (loading) return <p>Chargement…</p>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!detail) return null;
  const { reservation, buyer } = detail;
  const status = reservation.status as ReservationStatus;
  const timePassed = new Date(reservation.time).getTime() < Date.now();

  return (
    <>
      <Link to="/reservations">← Retour</Link>
      <h2 style={{ marginTop: 12 }}>{reservation.reservation_number}</h2>

      <div className="card">
        <div className="grid-2">
          <div>
            <div className="muted">Statut</div>
            <div><span className={`badge ${statusClass(status)}`}>{statusLabel(status)}</span></div>
          </div>
          <div>
            <div className="muted">Date et heure</div>
            <div><strong>{formatDateLong(reservation.date)} à {formatTime(reservation.time)}</strong></div>
          </div>
          <div>
            <div className="muted">Couverts</div>
            <div><strong>{reservation.covers}</strong></div>
          </div>
          <div>
            <div className="muted">Client</div>
            <div>{buyer?.name ?? buyer?.email ?? '—'}</div>
            <div className="muted">{buyer?.phone ?? buyer?.email}</div>
            {buyer && buyer.no_show_count >= 2 && <span className="badge red">⚠ {buyer.no_show_count} no-shows précédents</span>}
          </div>
        </div>
        {reservation.special_request && (
          <>
            <div className="muted" style={{ marginTop: 16 }}>Demande spéciale</div>
            <div>{reservation.special_request}</div>
          </>
        )}
        {reservation.cancelled_at && (
          <div className="alert alert-error" style={{ marginTop: 16 }}>
            Annulée le {new Date(reservation.cancelled_at).toLocaleString('fr-FR')}
          </div>
        )}
      </div>

      {(status === 'confirmed' || status === 'pending') && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Actions</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button className="danger" disabled={updating} onClick={() => setShowCancelInput(true)}>Annuler la réservation</button>
            {timePassed && (
              <button disabled={updating} onClick={() => {
                if (confirm('Marquer comme no-show ? Le compteur du client sera incrémenté.')) void changeStatus('no_show');
              }}>Marquer no-show</button>
            )}
          </div>
          {showCancelInput && (
            <div style={{ marginTop: 16 }}>
              <div className="form-row">
                <label>Motif d’annulation (visible par le client)</label>
                <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
              </div>
              <button className="danger" onClick={() => changeStatus('cancelled')} disabled={updating}>Confirmer l’annulation</button>{' '}
              <button onClick={() => setShowCancelInput(false)}>Retour</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function statusClass(s: ReservationStatus): string {
  if (s === 'confirmed') return 'green';
  if (s === 'cancelled') return 'red';
  if (s === 'no_show') return 'red';
  return 'orange';
}
function statusLabel(s: ReservationStatus): string {
  const m: Record<ReservationStatus, string> = {
    pending: 'En attente', confirmed: 'Confirmée', cancelled: 'Annulée', no_show: 'No-show',
  };
  return m[s];
}
function formatDateLong(date: string): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(new Date(`${date}T12:00:00.000Z`));
  } catch { return date; }
}
function formatTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
}
